import { FC, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import MDEditor from '@uiw/react-md-editor';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton';
import { useUpdateQuestion } from '@/api/useAdminPastPaper';
import { Database } from '@/_constants/database.types';

type QuestionRow = Database['public']['Tables']['questions']['Row'];

type QuestionContentEditorProps = {
  question: QuestionRow;
  pastPaperId: string;
};

const QuestionContentEditor: FC<QuestionContentEditorProps> = ({ question, pastPaperId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [contentText, setContentText] = useState('');
  const { mutate: updateQuestion, isPending } = useUpdateQuestion();

  const content = question.content;
  const truncated = content && content.length > 80 ? `${content.substring(0, 80)}...` : content;

  const handleOpen = () => {
    setContentText(question.content || '');
    setIsOpen(true);
  };

  const handleSave = () => {
    updateQuestion(
      {
        questionId: question.id,
        pastPaperId,
        updates: { content: contentText },
      },
      {
        onSuccess: () => setIsOpen(false),
      }
    );
  };

  return (
    <>
      <button
        onClick={handleOpen}
        className="text-left text-gray-600 text-sm hover:text-[#C87941] transition-colors"
      >
        {truncated || <span className="text-gray-400 italic">No content</span>}
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={() => setIsOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-4xl mx-4 my-8 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Edit Content - {question.category}
              </h2>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4" data-color-mode="light">
              <MDEditor
                value={contentText}
                onChange={(value) => setContentText(value || '')}
                height={400}
                preview="live"
                hideToolbar={false}
              />
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setIsOpen(false)}
                className="px-4 py-2 text-gray-700 font-medium hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <LoadingButton
                loading={isPending}
                onClick={handleSave}
                className="bg-[#0f172a] hover:bg-[#1e293b] text-white border-none px-6"
              >
                Save
              </LoadingButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuestionContentEditor;
